/* eslint-disable camelcase */
import * as yup from 'yup';

import {
  CUSTOMER_STATE,
  FOCUS,
  GROUP,
  LIFECYCLE_STAGE,
  MessageDetails,
  MESSAGE_TYPE,
  URGENCY,
} from './types';

// Enum value lists for `oneOf` constraints. Kept as plain arrays so yup's
// validator gets the literal values rather than a const-asserted readonly tuple.
// Nullable enum fields include `null` in their list so `.nullable()` lets the
// preset-B "everything null" payload pass `oneOf` instead of being rejected.
const customerStateValues: string[] = Object.values(CUSTOMER_STATE);
const lifecycleStageValues: string[] = Object.values(LIFECYCLE_STAGE);
const messageTypeValues: (string | null)[] = [
  ...Object.values(MESSAGE_TYPE),
  null,
];
const groupValues: (string | null)[] = [...Object.values(GROUP), null];
const urgencyValues: (string | null)[] = [...Object.values(URGENCY), null];
const focusValues: (string | null)[] = [...Object.values(FOCUS), null];

const nullableBoolean = yup.boolean().nullable().defined();
const nullableString = yup.string().nullable().default(null);

// Resolved runtime values. The API contract (RuntimeContext* serializers)
// makes every nested field Optional, and stored snapshots can carry empty
// strings (e.g. a product captured with a handle but no title). Partial data
// degrades to per-row fallbacks in the drawer — it must never invalidate the
// whole payload. Note yup's string().required() also rejects "", which is
// what hid live payloads behind the empty state.
const runtimeContextSchema = yup
  .object({
    offer: yup
      .object({
        title: nullableString,
        discount_value: nullableString,
        discount_code: nullableString,
      })
      .nullable()
      .default(null),
    products: yup
      .array()
      .of(
        yup.object({
          id: nullableString,
          title: nullableString,
          handle: nullableString,
          image_url: nullableString,
        }),
      )
      .default([]),
    content: yup
      .array()
      .of(
        yup.object({
          url: nullableString,
          destination: nullableString,
        }),
      )
      .default([]),
    tone_of_voice: yup
      .object({
        casual_formal: yup.number().nullable().defined(),
        gentle_bold: yup.number().nullable().defined(),
        product_lifestyle: yup.number().nullable().defined(),
        concise_chatty: yup.number().nullable().defined(),
        // Optional until the details endpoint serves it — .default(null)
        // (not .defined()) so current payloads without the key stay valid.
        relaxed_urgent: yup.number().nullable().default(null),
      })
      .nullable()
      .default(null),
  })
  .nullable()
  .default(null);

// Forward-compat: schema validates the v1 fields against Chase's contract plus
// the additive `runtime_context` extension. Other unknown additive fields pass
// through validation untouched — yup's default behavior is to allow extras.
export const messageDetailsSchema = yup
  .object({
    display_name: yup.string().required(),
    // .defined() (not .required()): the backend serves description as a
    // possibly-empty string, and yup's .required() rejects "", which
    // invalidated the whole payload and hid valid drawers behind the empty
    // state (PS-APP-PROD-6X1/6X0). Still required-present, just allows "".
    description: yup.string().defined(),
    parent_id: yup.string().nullable().defined(),
    full_reference_text: yup.string().nullable().defined(),
    customer_states: yup
      .array()
      .of(yup.string().oneOf(customerStateValues).required())
      .required(),
    lifecycle_stages: yup
      .array()
      .of(yup.string().oneOf(lifecycleStageValues).required())
      .required(),
    message_type: yup.string().oneOf(messageTypeValues).nullable().defined(),
    group: yup.string().oneOf(groupValues).nullable().defined(),
    urgency: yup.string().oneOf(urgencyValues).nullable().defined(),
    focus: yup.string().oneOf(focusValues).nullable().defined(),
    levers: yup.array().of(yup.string().required()).required(),
    is_conversational: nullableBoolean,
    references_previous_offer: nullableBoolean,
    offers_new_discount: nullableBoolean,
    price_increase: nullableBoolean,
    scarcity: nullableBoolean,
    timing_pressure: nullableBoolean,
    competition: nullableBoolean,
    runtime_context: runtimeContextSchema,
    verified: nullableBoolean,
  })
  .required();

// Returns the validation failure for a candidate payload, or null when it is
// valid. Exposed so callers can report contract drift (the error message names
// the failing field path) instead of silently rendering the empty state.
export const getMessageDetailsValidationError = (
  value: unknown,
): Error | null => {
  try {
    // strict: true disables yup's type coercion so e.g. `display_name: true`
    // can't get silently coerced to the string "true" and slip past validation.
    messageDetailsSchema.validateSync(value, { strict: true });
    return null;
  } catch (error) {
    return error instanceof Error ? error : new Error(String(error));
  }
};

// Type-predicate form so callers narrow `unknown` to `MessageDetails`
// without an `as` cast at the call site. The runtime check still defers
// to yup; yup loses the narrowed enum unions on `oneOf` in 0.32, which
// is why the predicate is needed in the first place.
export const isValidMessageDetails = (
  value: unknown,
): value is MessageDetails => getMessageDetailsValidationError(value) === null;

export const parseMessageDetails = (value: unknown): MessageDetails | null =>
  isValidMessageDetails(value) ? value : null;
